import { useEffect, useState, useRef } from "react";
import { Sparkles, Workflow, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { API, apiFetch } from "@/utils/api";
import { formatSignedCurrency, formatDateShort, formatCurrencyIn } from "@/utils/format";
import { confidenceLabel } from "@/components/ClassifySuggestion";
import { suggestionLabel } from "./MovementCard";

export default function MovementSheet({ tx, suggestion, categories, onClose, onClassified }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);

  const userPickedRef = useRef(false);
  const linkSeq = useRef(0);

  const payrollPayment = tx?.category === "nominas" && tx?.status === "pending";
  const isPending = tx?.status === "pending" && !payrollPayment;

  useEffect(() => {
    // Hoja nueva: se resetea la marca de elección manual.
    userPickedRef.current = false;
  }, [tx]);

  useEffect(() => {
    // Preselección: la sugerencia solo marca la categoría y SOLO si el
    // usuario no ha elegido nada a mano; NUNCA se aplica sola.
    if (userPickedRef.current) return;
    if (tx && suggestion?.category_key) setSelectedKey(suggestion.category_key);
    else setSelectedKey(null);
  }, [tx, suggestion]);

  useEffect(() => {
    // Conciliado: cargar la factura vinculada para la vista informativa.
    // linkSeq descarta respuestas obsoletas si se cambia rápido de movimiento.
    setLinkInfo(null);
    if (tx && tx.status === "reconciled") {
      const seq = ++linkSeq.current;
      (async () => {
        try {
          const res = await apiFetch(`${API}/bank/transactions/${tx.transaction_id}/invoice`);
          if (!res.ok) return;
          const d = await res.json();
          if (seq === linkSeq.current) setLinkInfo(d);
        } catch (e) {
          console.error("Error cargando factura vinculada", e);
        }
      })();
    }
  }, [tx]);

  if (!tx) return null;

  const visibleCategories = categories.filter((c) =>
    tx.amount < 0 ? c.kind === "gasto" : c.kind === "ingreso"
  );
  const selected = visibleCategories.find((c) => c.key === selectedKey) || null;

  const handleConfirm = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/reconciliation/classify`, {
        method: "POST",
        body: JSON.stringify({
          transaction_id: tx.transaction_id,
          account_code: selected.account_code,
          category: selected.key,
        }),
      });
      if (res.ok) {
        toast.success(selected.key === "nominas" ? "Pago de nómina · Sin conciliar" : `Clasificado: ${selected.label} — tú lo has confirmado`);
        onClassified(tx.transaction_id);
      } else if (res.status === 409) {
        toast.error("Este movimiento ya estaba clasificado o conciliado. Actualizando…");
        onClassified(tx.transaction_id);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo clasificar el movimiento");
      }
    } catch (e) {
      toast.error("Error de red al clasificar");
    } finally {
      setSaving(false);
    }
  };

  const invoice = linkInfo?.invoice;
  const classifiedLabel = suggestionLabel(
    { category_key: tx.classified_category, account_code: tx.classified_account },
    categories
  );

  return (
    <Sheet open={!!tx} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="bottom"
        data-testid="msheet"
        className="max-h-[80%] p-0 rounded-t-2xl flex flex-col"
      >
        <div className="px-4 pt-4 shrink-0">
          <SheetTitle className="font-heading text-lg font-bold text-slate-900">
            {isPending ? "Clasificar movimiento" : "Detalle del movimiento"}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {isPending ? "Elige la categoría y confirma" : "Información del movimiento"}
          </SheetDescription>
        </div>

        <div className="overflow-y-auto px-4 pb-4 grow">
          {/* Resumen del movimiento */}
          <div className="mt-3 bg-slate-50 border border-slate-100 rounded-xl p-3">
            <p className="text-[13.5px] font-medium text-slate-900">{tx.description}</p>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <span className="font-mono text-[11.5px] text-slate-500">{formatDateShort(tx.date)}</span>
              <span className={`font-mono text-[15px] font-semibold tabular-nums ${tx.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                {formatSignedCurrency(tx.amount)}
              </span>
            </div>
          </div>

          {isPending ? (
            <>
              {suggestion && (
                <div data-testid="msheet-sugg" className="mt-2.5 bg-violet-50 border border-violet-200 rounded-xl p-3">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-violet-800">
                    {suggestion.source === "ai" ? (
                      <><Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Sugerencia IA
                        {suggestion.confidence != null && ` — confianza ${confidenceLabel(suggestion.confidence)}`}</>
                    ) : (
                      <><Workflow className="w-3.5 h-3.5" strokeWidth={1.5} /> Regla aplicada</>
                    )}
                  </p>
                  <p className="mt-1.5 text-[13px] font-semibold text-violet-900">
                    {suggestionLabel(suggestion, categories)}
                  </p>
                  {suggestion.reason && (
                    <p className="mt-0.5 text-[11.5px] text-violet-700 leading-snug">{suggestion.reason}</p>
                  )}
                  <p className="mt-1.5 text-[11px] text-violet-600">
                    Preseleccionada abajo — confírmala o elige otra. Nunca se aplica sola.
                  </p>
                </div>
              )}

              <p className="mt-3.5 mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
                Elige categoría
              </p>
              <div className="grid grid-cols-2 gap-2">
                {visibleCategories.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`msheet-cat-${c.key}`}
                    onClick={() => {
                      userPickedRef.current = true;
                      setSelectedKey(c.key);
                    }}
                    className={`text-left rounded-xl p-3 min-h-[64px] border-[1.5px] ${
                      selectedKey === c.key ? "bg-blue-50 border-blue-600" : "bg-white border-slate-200"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-1.5">
                      <span className="text-[12.5px] font-semibold text-slate-900 leading-tight">{c.label}</span>
                      {c.account_code && <span className="font-mono text-[9.5px] text-slate-400 whitespace-nowrap">PGC {c.account_code}</span>}
                    </div>
                    <p className="mt-1 text-[10.5px] text-slate-500 leading-snug">{c.description}</p>
                  </button>
                ))}
              </div>
              <p className="mt-2.5 text-center text-[11px] text-slate-400">
                El plan contable completo (PGC) queda en escritorio; en móvil, categorías cotidianas.
              </p>
            </>
          ) : (
            <div data-testid="msheet-info" className="mt-2.5 bg-slate-50 border border-slate-100 rounded-xl p-3">
              {payrollPayment ? <p className="text-sm text-slate-700">Pago de nómina · Sin conciliar. No genera gasto ni asiento; la conciliación de nóminas todavía no está disponible.</p> : tx.status === "reconciled" ? (
                <>
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                    <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Conciliado con el banco
                  </p>
                  {invoice ? (
                    <p className="mt-1.5 text-[13px] text-slate-700">
                      Factura <span className="font-mono">{invoice.invoice_number}</span> ·{" "}
                      {invoice.supplier_name || invoice.client_name} ·{" "}
                      <span className="font-mono">{formatCurrencyIn(invoice.total_amount, invoice.currency)}</span>
                    </p>
                  ) : (
                    <p className="mt-1.5 text-[13px] text-slate-500">Cargando factura vinculada…</p>
                  )}
                  <p className="mt-2 text-[11.5px] text-slate-500">
                    El cobro/pago solo se deshace desde Conciliación (escritorio).
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold text-blue-700">Clasificado</p>
                  <p className="mt-1.5 text-[13px] text-slate-700">{classifiedLabel}</p>
                  <p className="mt-2 text-[11.5px] text-slate-500">
                    Se reclasifica o deshace desde Conciliación (escritorio).
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {isPending && (
          <div className="shrink-0 px-4 py-3 border-t border-slate-100 bg-white">
            <button
              data-testid="msheet-confirm"
              onClick={handleConfirm}
              disabled={!selected || saving}
              className={`w-full min-h-[50px] rounded-2xl text-[15px] font-semibold text-white ${
                selected && !saving ? "bg-slate-900" : "bg-slate-300"
              }`}
            >
              {saving ? "Guardando…" : selectedKey === "nominas" ? "Identificar pago de nómina" : "Clasificar"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
